import { Game } from "@aura-frontend/omnibus-az-client";

/**
 * Extracts home/away team names from a Game. Prefers parsing the
 * `generated_display_name` or `displayName` ("Home x Away" / "Home vs Away"),
 * falling back to the raw `teams.home`/`teams.away` UUIDs.
 */
export const getTeamNames = (
  event: Game,
): { homeTeamName: string; awayTeamName: string } => {
  const pickSeparator = (value?: string): "x" | "vs" | undefined => {
    if (!value) return undefined;
    if (value.includes("x")) return "x";
    if (value.includes("vs")) return "vs";
    return undefined;
  };

  let nameProperty: string | undefined;
  let separator = pickSeparator(event.generated_display_name);
  if (separator) {
    nameProperty = event.generated_display_name;
  } else {
    separator = pickSeparator(event.displayName);
    if (separator) {
      nameProperty = event.displayName;
    }
  }

  if (!nameProperty && event.teams.home && event.teams.away) {
    return {
      homeTeamName: "Home",
      awayTeamName: "Away",
    };
  }

  const parts = separator ? nameProperty?.split(separator) : undefined;
  const homeTeamName = parts?.[0].trim() || event.teams.home;
  const awayTeamName = parts?.[1].trim() || event.teams.away;

  return { homeTeamName, awayTeamName };
};

/**
 * Returns the event's start/end as epoch milliseconds. Falls back to `Date.now()`
 * when start time is missing; end is `undefined` when not set.
 */
export const getEventTimes = (
  event: Game,
): { startTime: number; endTime?: number } => {
  const first = event.times[0];
  const last = event.times.at(-1);
  const startTime = first?.startTime;
  const endTime = last?.endTime ?? undefined;

  return {
    startTime: startTime != null ? startTime * 1000 : Date.now(),
    endTime: endTime ? Number(endTime) * 1000 : undefined,
  };
};

/**
 * Formats the remaining time until `endDate` (epoch ms) as "HH:MM:SS".
 * Returns "00:00:00" once the end time has passed.
 */
export const formatTimeRemaining = (endDate: number): string => {
  const diff = new Date(endDate).getTime() - Date.now();
  if (diff <= 0) return "00:00:00";
  const h = Math.floor(diff / 3_600_000);
  const m = Math.floor((diff % 3_600_000) / 60_000);
  const s = Math.floor((diff % 60_000) / 1_000);
  return [h, m, s].map((v) => String(v).padStart(2, "0")).join(":");
};

/**
 * Formats an epoch-ms timestamp for event card display, e.g. "Mon, Jan 15 · 2:30 PM".
 */
export const formatEventDateTime = (startDate: number): string => {
  const d = new Date(startDate);
  return (
    d.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
    }) +
    " · " +
    d.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
    })
  );
};
